<template>
  <div class='lib__form' data-cy='charging-station-declaration-form'>
    <header class='lib__form__header'>
      <main-title>{{ $t('chargingStationDeclaration.title') }}</main-title>

      <breadcrumb
        :currentStep='currentStep'
        :isLastStepActive='false'
        :lastStep='lastStep'
        :lastStepSeen='lastStepSeen'
        :stepDefinitions='stepDefinitions'
        @goToStep='goToStepWithValidation'
        @reset='resetForm' />
    </header>

    <ApiErrors />

    <DialogBox />

    <back-to-summary :isButtonShowed='currentStep < 4 && lastStepSeen === 4' :summaryStepNumber='4'
                     @backToSummary='goToStepWithValidation' />

    <IdentificationStep v-if='currentStep === 1' ref='step1' @canContinue='canContinue = $event' @next='nextStep' />

    <ContactInfoStep v-if='currentStep === 2' ref='step2' @canContinue='canContinue = $event' @next='nextStep'
                     @previous='previousStep' :showCivility="true" />

    <ChargingStationInformationStep v-if='currentStep === 3' ref='step3' @canContinue='canContinue = $event'
                                    @next='nextStep'
                                    @previous='previousStep' />

    <SummaryStep v-if='currentStep === 4' ref='step4' :readOnly='lastStepSeen > 4' @canContinue='canContinue = $event'
                 @goToStep='goToStep' @next='nextStep' @previous='previousStep'></SummaryStep>

    <send-confirmation v-if='currentStep === lastStep' :email='sendConfirmationEmail'
                       :multiple-send='multipleSend' :help-text-url="$t('chargingStationDeclaration.confirmation.helpTextUrl')"
                       :title="$t('chargingStationDeclaration.title')"></send-confirmation>

    <StepActions
      :can-continue='canContinue'
      :has-next='currentStep < 5'
      :has-previous='currentStep !== 1 && currentStep !== 5'
      :has-print='false'
      :has-reset='false'
      :loading='loading'
      :nextLabel='nextLabel'
      @next='tryNextStep'
      @previous='tryPreviousStep'
      @reset='resetForm' />
  </div>
</template>

<script lang='ts'>
import { Component } from 'vue-property-decorator'
import StepActions from '@/components/shared/StepActions.vue'
import { Form } from './Form'
import { StepDefinition } from '@ores/vue-library'
import ApiErrors from '@/components/display/ApiErrors.vue'
import IdentificationStep from '@/components/steps/charging-station-declaration/IdentificationStep.vue'
import ContactInfoStep from '@/components/steps/charging-station-declaration/ContactInfoStep.vue'
import ChargingStationInformationStep
  from '@/components/steps/charging-station-declaration/ChargingStationInformationStep.vue'
import SummaryStep from '@/components/steps/charging-station-declaration/SummaryStep.vue'
import SendConfirmation from '@/components/inputs/ChargingStationDeclaration/SendConfirmation.vue'
import DialogBox from '@/components/shared/DialogBox.vue'
import { removeInterceptors } from '@/plugins/http-interceptors-global'
import store from '@/store'
import Step from '@/components/steps/Step'
import {
  addChargingStationDeclarationResponseInterceptors
} from '@/plugins/http-response-interceptors-charging-station-declaration'
import { ChargingStationDeclarationGridAccessPoint, ContactInfo } from '@/models'
import { State } from 'vuex-class'
import { contactService } from '@/services'

@Component({
  components: {
    ChargingStationInformationStep,
    IdentificationStep,
    ContactInfoStep,
    SummaryStep,
    StepActions,
    SendConfirmation,
    DialogBox,
    ApiErrors
  },
  name: 'charging-station-declaration'
})
export default class ChargingStationDeclaration extends Form {
  //#region [Data]
  public stepDefinitions: StepDefinition[] = [
    new StepDefinition('identification', 1),
    new StepDefinition('contact', 2),
    new StepDefinition('station', 3),
    new StepDefinition('summary', 4),
    new StepDefinition('confirm', 5)
  ]
  public isIdentified: boolean = false

  @State('gridAccessPoint', { namespace: 'chargingStationDeclaration/identification' })
  public gridAccessPointState!: ChargingStationDeclarationGridAccessPoint
  @State('contactInfo', { namespace: 'chargingStationDeclaration/contactInfo' })
  public contactInfoState!: ContactInfo | null
  // #endregion

  public get multipleSend(): boolean {
    return this.contactInfoState !== null && this.contactInfoState.otherContacts.length > 0
  }

  public get sendConfirmationEmail(): string {
    let email: string = ''
    if (this.contactInfoState) {
      email += contactService.getContactEmail(this.contactInfoState)
      for (const otherContact of this.contactInfoState.otherContacts) {
        email += ', ' + otherContact.email
      }
    }
    return email
  }

  //#region [Computed]
  private get nextLabel(): string | null {
    if (this.currentStep === 4) {
      return this.$t('common.confirm').toString()
    }

    return null
  }

  //#endregion

  //#region [Mutation]
  //#endregion

  //#region [Method]
  public beforeCreate(): void {
    document.title = this.$t('chargingStationDeclaration.title').toString()
  }

  public mounted() {
    this.currentStep = 1
    this.lastStepSeen = 1
    this.stepToGoAfterValidation = 4
    this.stepsToValidate = []

    this.onLangChanged(this.lang)

    if (this.interceptorId !== null) {
      removeInterceptors(this.$api, this.interceptorId)
    }
    const interceptorId = addChargingStationDeclarationResponseInterceptors(this.$api, store)
    this.setInterceptorId(interceptorId)

    // Check maintenance
    this.$api.get('api/maintenanceManagement/charging-station-declaration')
  }

  public getStepComponent(step: number): Step | null {
    return (this.$refs[`step${this.currentStep}`] as Step) || null
  }

  public resetForm() {
    this.lastStepSeen = 1
    this.$store.dispatch('chargingStationDeclaration/reset')
    this.$nextTick(() => (this.currentStep = 1))
  }

  //#endregion

  //#region [Watch]
  //#endregion
}
</script>
